import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ArrowUpRight } from 'lucide-react';
import Navigation from '../components/layout/Navigation';
import Footer from '../components/layout/Footer';
import styles from './FAQ.module.css';

const FAQ_GROUPS = [
  {
    id: 'orders',
    label: 'Orders & quotes',
    items: [
      {
        q: 'How do I place a bulk order?',
        a: 'Share your product type, GSM, dimensions, quantity, and delivery city through our contact form or call our sales team. We confirm specs, pricing, and lead time before issuing a quotation or purchase order.',
      },
      {
        q: 'Is there a minimum order quantity (MOQ)?',
        a: 'MOQ varies by product — rolls, sleeves, wrap sheets, and custom print runs have different thresholds. For repeat B2B buyers we often align MOQ with your monthly consumption so reorders stay practical.',
      },
      {
        q: 'How long is a quotation valid?',
        a: 'Quotes typically remain valid for 7–15 days depending on raw-material movement and quantity. Your account manager will mention the exact validity on every quote.',
      },
    ],
  },
  {
    id: 'products',
    label: 'Products & specs',
    items: [
      {
        q: 'What paper products do you manufacture?',
        a: 'Our range includes PE coated rolls, food wrap paper, cup sleeves, ripple sleeves, paper plates, packaging papers, bobbins, jumbo rolls, and custom roll stock for converting lines.',
      },
      {
        q: 'Can you match a specific GSM or roll width?',
        a: 'Yes. GSM, width, coating type, and reel size are discussed upfront so production matches your die, line speed, and packing setup. Share a sample or spec sheet for faster alignment.',
      },
      {
        q: 'Do you offer custom printing and branding?',
        a: 'We support branded cup sleeves, wraps, and packaging with artwork supplied by you. Print registration, colour approval, and repeat-run consistency are handled with your marketing or procurement team.',
      },
    ],
  },
  {
    id: 'quality',
    label: 'Quality & compliance',
    items: [
      {
        q: 'Are your materials food-grade?',
        a: 'Food-contact grades are produced with suitable raw materials and batch-wise checks before dispatch. Product-specific compliance details are shared during quotation for your category.',
      },
      {
        q: 'How is quality checked before dispatch?',
        a: 'We run batch-wise QC on GSM, coating adhesion, reel tightness, print finish, and visual defects. Critical parameters are agreed with you for repeat orders so every lot stays within tolerance.',
      },
    ],
  },
  {
    id: 'dispatch',
    label: 'Dispatch & pan-India supply',
    items: [
      {
        q: 'Do you deliver across India?',
        a: 'Yes — we dispatch pan-India from our Meerut facility to distributors, QSR chains, converters, and industrial buyers. Delivery timelines depend on quantity, destination, and transport mode.',
      },
      {
        q: 'Can you support repeat dispatch schedules?',
        a: 'Many clients run monthly or fortnightly repeat schedules. We plan production and logistics around your forecast so stock gaps are reduced during peak demand.',
      },
      {
        q: 'What are typical lead times?',
        a: 'Standard grades often ship within agreed commercial lead times after PO confirmation. Custom print or special GSM may need additional production days — we confirm this before you confirm the order.',
      },
    ],
  },
];

const FAQ = () => {
  const [activeGroup, setActiveGroup] = useState(FAQ_GROUPS[0].id);
  const [openId, setOpenId] = useState(`${FAQ_GROUPS[0].id}-0`);

  const currentGroup = FAQ_GROUPS.find((g) => g.id === activeGroup) ?? FAQ_GROUPS[0];

  const toggleItem = (groupId, index) => {
    const id = `${groupId}-${index}`;
    setOpenId((prev) => (prev === id ? null : id));
  };

  return (
    <>
      <Navigation />
      <main className={styles.page}>
        <section className={styles.hero}>
          <div className={styles.container}>
            <span className={styles.badge}>Help centre</span>
            <h1 className={styles.title}>
              Frequently Asked <span className={styles.titleAccent}>Questions</span>
            </h1>
            <p className={styles.subtitle}>
              Quick answers on bulk orders, product specs, quality checks, and pan-India dispatch from
              Greeneri Paper Tradelinks, Meerut.
            </p>
          </div>
        </section>

        <section className={styles.content}>
          <div className={styles.container}>
            <div className={styles.layout}>
              <aside className={styles.sidebar}>
                <p className={styles.sidebarLabel}>Browse by topic</p>
                <ul className={styles.topicList}>
                  {FAQ_GROUPS.map((group) => (
                    <li key={group.id}>
                      <button
                        type="button"
                        className={styles.topicBtn}
                        data-active={activeGroup === group.id}
                        onClick={() => {
                          setActiveGroup(group.id);
                          setOpenId(`${group.id}-0`);
                        }}
                      >
                        {group.label}
                      </button>
                    </li>
                  ))}
                </ul>

                <div className={styles.sidebarCta}>
                  <p>Still have questions?</p>
                  <Link to="/contact" className={styles.sidebarCtaLink}>
                    Contact our team
                    <ArrowUpRight size={15} strokeWidth={2.5} aria-hidden="true" />
                  </Link>
                </div>
              </aside>

              <div className={styles.panel}>
                <h2 className={styles.panelTitle}>{currentGroup.label}</h2>
                <ul className={styles.faqList}>
                  {currentGroup.items.map((item, index) => {
                    const itemId = `${currentGroup.id}-${index}`;
                    const isOpen = openId === itemId;
                    return (
                      <li key={itemId} className={styles.faqItem} data-open={isOpen}>
                        <button
                          type="button"
                          className={styles.faqTrigger}
                          aria-expanded={isOpen}
                          onClick={() => toggleItem(currentGroup.id, index)}
                        >
                          <span>{item.q}</span>
                          <ChevronDown
                            size={20}
                            strokeWidth={2.25}
                            className={styles.faqChevron}
                            aria-hidden="true"
                          />
                        </button>
                        <AnimatePresence initial={false}>
                          {isOpen && (
                            <motion.div
                              className={styles.faqAnswerWrap}
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.25, ease: 'easeInOut' }}
                            >
                              <p className={styles.faqAnswer}>{item.a}</p>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            <div className={styles.bottomCta}>
              <div className={styles.bottomCtaCopy}>
                <strong>Need a quote for your line?</strong>
                <span>Share specs and quantity — our Meerut team responds with clear timelines.</span>
              </div>
              <Link to="/contact" className={styles.bottomCtaBtn}>
                Get a quote
                <ArrowUpRight size={16} strokeWidth={2.5} aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default FAQ;
